import { Button } from '@/shared/components/ui/button';

const WELCOME_VIDEO_URL = import.meta.env.VITE_CARE_SEEKER_WELCOME_VIDEO_URL;

interface WelcomeLetterProps {
  onClose: () => void;
}

export function WelcomeLetter({ onClose }: WelcomeLetterProps) {
  // Convert YouTube watch URL to embed URL if needed
  const getVideoEmbedUrl = (url: string) => {
    if (url.includes('youtube.com/watch?v=')) {
      const videoId = url.split('v=')[1]?.split('&')[0];
      return `https://www.youtube.com/embed/${videoId}`;
    }
    if (url.includes('youtu.be/')) {
      const videoId = url.split('youtu.be/')[1]?.split('?')[0];
      return `https://www.youtube.com/embed/${videoId}`;
    }
    return url;
  };

  const isYouTubeUrl =
    !!WELCOME_VIDEO_URL &&
    (WELCOME_VIDEO_URL.includes('youtube.com') || WELCOME_VIDEO_URL.includes('youtu.be'));

  // Match image extensions case-insensitively and allow the URL to end with
  // the extension OR be followed by a query string / fragment
  // (e.g. "/welcome.JPG", "/welcome.jpg?v=1", "/welcome.jpg#frag").
  const isImageUrl =
    !!WELCOME_VIDEO_URL && /\.(jpe?g|png|gif|webp)(?:$|[?#])/i.test(WELCOME_VIDEO_URL);

  const videoUrl = WELCOME_VIDEO_URL ? getVideoEmbedUrl(WELCOME_VIDEO_URL) : '';

  return (
    <div className="min-h-screen bg-[#026857] py-8 px-4 overflow-y-auto">
      {/* Logo in top left */}
      <div className="absolute top-6 left-6">
        <img
          src="/bquest-logo.png"
          alt="bQuest Logo"
          className="h-12 w-auto"
        />
      </div>

      {/* Content Container */}
      <div className="max-w-4xl mx-auto pt-20">
        {/* Title */}
        <h1 className="text-3xl sm:text-4xl font-bold text-center mb-8 text-white">
          Welcome — You're In The Right Place
        </h1>

        {/* Letter Card */}
        <div className="bg-[#f9f6f1] rounded-lg shadow-2xl p-8 sm:p-12">
          {/* Letter Text */}
          <div className="space-y-4 text-gray-700 leading-relaxed text-base sm:text-lg">
            <p className="font-bold text-lg sm:text-xl">
              A Note from our founders,
            </p>

            <p>
              We built this because we've lived it—from both sides of the journey.
            </p>

            <p>
              Some of us arrived here as caregivers, suddenly responsible for decisions we never expected to make. Others came as individuals planning thoughtfully for our own future, seeking clarity, dignity, and a sense of control. What we shared was the same feeling: <em>Why is this so hard? Why does it feel so isolating?</em>
            </p>

            <p>
              We remember the late-night searching, the uncertainty, and the fear of making the wrong choice—for ourselves or for someone we love. bQuest was created from that shared experience. We built the platform we wished had existed: a place grounded in empathy, designed to bring clarity, and to connect you to trusted guidance, people, and information—so navigating care feels more supported and less overwhelming.
            </p>

            <p>
              This is a space designed to support you—whether you want guidance, information, or time to explore.
            </p>

            <p className="font-medium mt-8 text-gray-800">
              Here for you,
              <br />
              Kelly, Lauren, and the bQuest team
            </p>
          </div>

          {/* Video/Image Section */}
          {WELCOME_VIDEO_URL && (
            <div className="mt-8">
              {isImageUrl ? (
                <img
                  className="w-full rounded-lg shadow-lg"
                  src={WELCOME_VIDEO_URL}
                  alt="Kelly and Lauren share our story"
                  loading="lazy"
                  decoding="async"
                />
              ) : isYouTubeUrl ? (
                <iframe
                  className="w-full rounded-lg shadow-lg aspect-video"
                  src={videoUrl}
                  title="Kelly and Lauren share our story"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              ) : (
                <video
                  className="w-full rounded-lg shadow-lg"
                  controls
                  preload="metadata"
                >
                  <source src={videoUrl} type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              )}
            </div>
          )}
        </div>

        {/* Get Started Button */}
        <div className="flex justify-center mt-10">
          <Button
            onClick={onClose}
            size="lg"
            className="bg-[#E2A807] hover:bg-[#C99006] text-white px-12 py-6 text-lg font-semibold"
          >
            Get Started
          </Button>
        </div>
      </div>
    </div>
  );
}
